import { get } from 'lodash-es'

/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

import axios from 'axios'

window.axios = axios

window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
window.axios.defaults.withCredentials = true
window.axios.defaults.withXSRFToken = true

// Token refresh state
let isRefreshing = false
let refreshSubscribers = []
let refreshAttempts = 0
const MAX_REFRESH_ATTEMPTS = 3
let cachedToken = null

// Get XSRF token from cookie
const getXsrfTokenFromCookie = () => {
  if (cachedToken) return cachedToken
  const cookies = document.cookie.split(';')
  const tokenCookie = cookies.find(cookie =>
    cookie.trim().startsWith('XSRF-TOKEN='),
  )
  cachedToken = tokenCookie
    ? decodeURIComponent(tokenCookie.split('=')[1])
    : null
  return cachedToken
}

// Invalidate cache
const invalidateTokenCache = () => {
  cachedToken = null
}

// Set XSRF token directly
const setXsrfToken = token => {
  if (token) {
    window.axios.defaults.headers.common['X-XSRF-TOKEN'] = token
    cachedToken = token
  }
}

// Sync token header
const syncTokenHeader = () => {
  const token = getXsrfTokenFromCookie()
  if (token) {
    window.axios.defaults.headers.common['X-XSRF-TOKEN'] = token
  }
}

// Subscribe failed requests to the token refresh
const subscribeTokenRefresh = callback => refreshSubscribers.push(callback)

// Retry failed requests with new token
const retryFailedRequests = () => {
  refreshSubscribers.forEach(callback => callback())
  refreshSubscribers = []
}

// Clear refresh state and subscribers
const clearRefreshState = () => {
  isRefreshing = false
  refreshSubscribers = []
  refreshAttempts = 0
  invalidateTokenCache()
}

// Check if URL should be excluded from refresh logic
const shouldExcludeFromRefresh = (url, method) => {
  if (!url) return false
  const alwaysExcluded = ['/csrf-token']
  if (alwaysExcluded.some(path => url.includes(path))) return true

  // Exclude ONLY the GET to /login (page itself), not the POST
  return url.includes('/login') && method?.toUpperCase() === 'GET'
}

// Refresh CSRF token
const refreshCsrfToken = async () => {
  if (isRefreshing) {
    return
  }

  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    throw new Error('Max refresh attempts reached')
  }

  isRefreshing = true
  refreshAttempts++

  try {
    const response = await axios.get('/csrf-token')
    const newToken = response.data?.csrf_token

    if (newToken) {
      setXsrfToken(newToken)
    } else {
      syncTokenHeader()
    }

    isRefreshing = false
    retryFailedRequests()
    refreshAttempts = 0 // Reset on success

    return true
  } catch (error) {
    isRefreshing = false
    throw error
  }
}

// Add request interceptor to ensure token is synced before each request
window.axios.interceptors.request.use(config => {
  syncTokenHeader()
  return config
}, (error) => {
  return Promise.reject(error)
}, {
  runWhen: () => cachedToken !== null,
})

window.axios.interceptors.response.use(
  response => response,
  async err => {
    const status = get(err, 'response.status')
    const config = err.response?.config
    const url = config?.url

    // Handle CSRF token mismatch (419) or unauthorized (401)
    if ((status === 419 || status === 401) && config) {
      if (shouldExcludeFromRefresh(url, config.method)) {
        return Promise.reject(err)
      }

      if (!isRefreshing) {
        try {
          await refreshCsrfToken()
        } catch (refreshError) {
          clearRefreshState()
          if (status === 401 || refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
            window.location.href = '/login'
          }
          return Promise.reject(refreshError)
        }
      }

      // Create new promise to retry original request
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(async () => {
          try {
            syncTokenHeader()
            const response = await window.axios(config)
            resolve(response)
          } catch (retryError) {
            reject(retryError)
          }
        })
      })
    }

    return Promise.reject(err)
  },
)

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */

// import Echo from 'laravel-echo';

// window.Pusher = require('pusher-js');

// window.Echo = new Echo({
//     broadcaster: 'pusher',
//     key: process.env.MIX_PUSHER_APP_KEY,
//     cluster: process.env.MIX_PUSHER_APP_CLUSTER,
//     forceTLS: true
// });
